import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { DataTable } from '@/components/ui/datatable';
import { riskRegisterColumnConfig } from '@/config/tableColumnConfig';
import { getRiskColumns } from './components/columns';
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from '@/components/ui/input';
import { Plus, Edit, Eye, Upload, Download, Search, ChevronDown, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import './risk-register.css';
import { Risk } from '@/types/risks';
import { getAuthUID, isAuthLoaded, onAuthReady } from '@/lib/authState';
import { useTenancy } from '@/contexts/TenancyContext';
import { getAllRisks, putRisk } from '@/services/risksService';
import { toast } from 'sonner';
import { createRisk } from '../../../services/risksService';
import { getHelpContentByPage } from '@/services/HelpPopoverService';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import { renderDate } from '../../../../shared/renderDate';
import { routePathByName } from '@/app/routes';

// Utility to map criticality/risk to badge variant
const getBadgeVariant = (level: string) => {
  switch (level.toLowerCase()) {
    case 'low':
      return 'low';
    case 'medium':
      return 'medium';
    case 'high':
      return 'high';
    case 'extreme':
      return 'extreme';
    default:
      return 'default';
  }
};

interface RiskRegisterProps {
  basePath?: string;
  isReadOnly?: boolean;
}

const RiskRegister: React.FC<RiskRegisterProps> = ({ basePath = '/risk-detail', isReadOnly = false }) => {
  const [uid, setUid] = useState<string | null>(isAuthLoaded() ? getAuthUID() : null);
  const { activeTenancy, userIdToName } = useTenancy();
  const tenancyId = activeTenancy?.id || window.companyID;
  const navigate = useNavigate();

  const [riskList, setRiskList] = useState<Risk[]>([])
  const [isLoading, setIsLoading] = useState(true);

  const theme = useTheme();
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState('');
  const [riskRatingFilter, setRiskRatingFilter] = useState<'all' | 'High' | 'Medium' | 'Low'>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | string>('all');
  const [categoryOptions, setCategoryOptions] = useState<Array<string>>([]);
  const [responseFilter, setResponseFilter] = useState<'all' | string>('all');
  const [responseOptions, setResponseOptions] = useState<Array<string>>([]);
  const [riskRatingOptions, setRiskRatingOptions] = useState<Array<string>>(['High', 'Medium', 'Low']);

  const [helpContent, setHelpContent] = useState(null);

  // Filtering
  const filteredRisks = useMemo(() => {
    return riskList.filter(a => {
      const matchesSearch = a.risk?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRiskRating = riskRatingFilter === 'all' ? true : a.riskRating?.toLowerCase() === riskRatingFilter.toLowerCase();
      const matchesCategory = categoryFilter === 'all' ? true : a.category?.toLowerCase() === categoryFilter.toLowerCase();
      const matchesResponse = responseFilter === 'all' ? true : a.response?.toLowerCase() === responseFilter.toLowerCase();
      return matchesSearch && matchesRiskRating && matchesCategory && matchesResponse;
    });
  }, [riskList, searchTerm, riskRatingFilter, categoryFilter, responseFilter]);

  // Sorting logic placeholder
  const [sortKey, setSortKey] = useState<keyof Risk>('category');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const sortedRisks = useMemo(() => {
    return [...filteredRisks].sort((a, b) => {
      if (a[sortKey] < b[sortKey]) return sortDir === 'asc' ? -1 : 1;
      if (a[sortKey] > b[sortKey]) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRisks, sortKey, sortDir]);

  const handleSort = (key: keyof Risk) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Import risks from an Excel file (XLSX)
  const normalizeControlList = (value: any): string[] => {
    if (value === null || value === undefined || value === '') return [];
    if (Array.isArray(value)) {
      return value
        .flatMap((entry) => normalizeControlList(entry))
        .map((entry) => entry.trim())
        .filter((entry) => entry.length);
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return normalizeControlList(parsed);
        }
      } catch {}
      return trimmed
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry.length);
    }
    if (typeof value === 'object') {
      try {
        return normalizeControlList(JSON.stringify(value));
      } catch {}
    }
    return [String(value)].map((entry) => entry.trim()).filter((entry) => entry.length);
  };

  const handleImportXLSX = () => {
    if (!tenancyId) {
      toast.error('No active tenancy selected');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        // Dynamically import SheetJS to keep initial bundle small
        let XLSX: any;
        try {
          XLSX = await import('xlsx');
        } catch (err) {
          toast.error('Excel import requires the "xlsx" package. Please install it with "npm i xlsx".');
          return;
        }

        const data = await file.arrayBuffer();
        const wb = XLSX.read(data, { type: 'array' });
        const sheetName = wb.SheetNames[0];
        if (!sheetName) throw new Error('No sheets found in the workbook');
        const ws = wb.Sheets[sheetName];
        const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (!rows.length) throw new Error('Sheet contains no data');

        const headers: string[] = (rows[0] as any[]).map((h: any) => String(h ?? '').trim());
        const dataRows: any[][] = rows.slice(1).filter(r => Array.isArray(r) && r.some(cell => cell != null && String(cell).trim() !== ''));

        // Coerce common boolean text values from spreadsheet cells
        const coerceFromCell = (val: any) => {
          if (val === null || val === undefined) return '';
          if (typeof val === 'boolean') return val; // keep as boolean
          if (typeof val === 'number') return val;  // keep numbers
          const trimmed = String(val).trim();
          if (trimmed === '') return '';
          const lower = trimmed.toLowerCase();
          if (lower === 'yes' || lower === 'true') return true;
          if (lower === 'no' || lower === 'false') return false;
          return trimmed;
        };

        const imported = dataRows.map(cols => {
          const obj: Record<string, any> = {};
          headers.forEach((h, idx) => {
            obj[h] = coerceFromCell(cols[idx]);
          });
          return obj;
        });

        const normalizedImported: Record<string, any>[] = imported.map((risk: Record<string, any>) => ({
          ...risk,
          selectedControls: normalizeControlList(risk.selectedControls),
          relatedControls: normalizeControlList(risk.relatedControls),
        }));

        // Validate required fields for each risk
        const valid = normalizedImported.every((r: any) => r.risk && r.category);
        if (!valid) throw new Error('Some risks are missing required fields (risk or category)');

        // --- Backend Sync: Persist imported risks --- (same as CSV path)
        let currentRisks = [...riskList];
        const toPlain = (obj: any) => JSON.parse(JSON.stringify(obj));
        const normalizeId = (val: any) => String(val ?? '').replace(/\D/g, '');
        const existingRiskIds = new Set(currentRisks.map(risk => String(risk.riskID || '').trim()));
        const existingRiskIdsDigits = new Set(currentRisks.map(risk => normalizeId(risk.riskID ?? risk.componentId)));
        const existingByComponent = new Map(
          currentRisks
            .filter(r => r.componentId)
            .map(r => [String(r.componentId), r])
        );

        console.log('Imported risks:', normalizedImported);
        console.log('Current risks:', currentRisks);
        console.log('Existing risk IDs:', existingRiskIds);

        // Track max ID for new risks (normalize to digits only) — compute from existing risks only
        let maxId = 0;
        currentRisks.forEach((risk: any) => {
          const raw = String(risk.riskID ?? risk.componentId ?? '');
          const digits = raw.replace(/\D/g, '');
          const idNum = parseInt(digits || '0', 10);
          if (!isNaN(idNum) && idNum > maxId) maxId = idNum;
        });

        for (const importedRisk of normalizedImported) {
          // Normalize incoming ID by stripping non-digits (handles RSK-123, spaces, etc.)
          const rawId = String(importedRisk.riskID ?? '').trim();
          const normalizedFromRiskId = rawId.replace(/\D/g, '');
          const rawComponent = String(importedRisk.componentId ?? '').trim();
          const normalizedFromComponent = rawComponent.replace(/\D/g, '');

          let resolvedId = normalizedFromRiskId || normalizedFromComponent || '';

          // If we can match an existing by componentId, prefer that
          if (!resolvedId && rawComponent && existingByComponent.has(rawComponent)) {
            const match = existingByComponent.get(rawComponent);
            resolvedId = String(match?.riskID || '').trim();
          }

          const resolvedDigits = normalizeId(resolvedId);
          const existsById = (resolvedId && existingRiskIds.has(resolvedId)) || (resolvedDigits && existingRiskIdsDigits.has(resolvedDigits));
          const existsByComponent = rawComponent && existingByComponent.has(rawComponent);

          if (!existsById && !existsByComponent) {
            // New risk → prefer Excel-provided riskID if present, else allocate next
            if (normalizedFromRiskId) {
              resolvedId = normalizedFromRiskId;
            } else {
              maxId++;
              resolvedId = maxId.toString();
            }
            importedRisk.riskID = parseInt(resolvedId);

            const newRisk = {
              ...importedRisk,
              componentId: importedRisk.componentId || `RSK-${resolvedId}`,
              riskIDstatus: 'active',
              selectedControls: normalizeControlList(importedRisk.selectedControls),
              relatedControls: normalizeControlList(importedRisk.relatedControls),
            };
            const newRiskPlain = toPlain(newRisk);

            try {
              console.debug('[Risk Import] CREATE', { resolvedId, importedRisk });
              await createRisk(tenancyId, resolvedId, newRiskPlain as any);
              existingRiskIds.add(resolvedId);
              if (newRisk.componentId) existingByComponent.set(String(newRisk.componentId), newRisk as any);
            } catch (err) {
              console.error('Failed to create new risk (XLSX):', err);
            }
          } else {
            // Update existing risk using normalized ID
            const updateId = existsById ? (resolvedDigits || resolvedId) : normalizedFromComponent;
            try {
              console.debug('[Risk Import] UPDATE', { updateId, importedRisk });
              const updatePayload = {
                ...importedRisk,
                riskID: parseInt(updateId),
                componentId: importedRisk.componentId || `RSK-${updateId}`,
                selectedControls: normalizeControlList(importedRisk.selectedControls),
                relatedControls: normalizeControlList(importedRisk.relatedControls),
              };
              const plainUpdate = toPlain(updatePayload);
              await putRisk(tenancyId, updateId, plainUpdate as any);
            } catch (err) {
              console.error('Failed to update risk (XLSX):', err);
            }
          }
        }

        // Reload risks from backend
        getAllRisks(tenancyId)
          .then((risks) => {
            const nonDeletedRisks = risks.filter((risk: any) => risk.riskIDstatus !== 'deleted');
            setRiskList(nonDeletedRisks);

            // Update filter options
            setRiskRatingOptions(Array.from(new Set(nonDeletedRisks.map(a => a.riskRating).map(a => a.toLowerCase()).map(a => a.charAt(0).toUpperCase() + a.slice(1)))));
            setCategoryOptions(Array.from(new Set(nonDeletedRisks.map(a => a.category).map(a => a.toLowerCase()).map(a => a.charAt(0).toUpperCase() + a.slice(1)))));
            setResponseOptions(Array.from(new Set(nonDeletedRisks.map(a => a.response).map(a => a.toLowerCase()).map(a => a.charAt(0).toUpperCase() + a.slice(1)))));

            toast.success('Risks imported from Excel and synced successfully');
          })
          .catch(err => {
            console.error('Failed to refresh risks after XLSX import:', err);
            toast.error('Imported risks but failed to refresh the display');
          });
      } catch (err: any) {
        toast.error('Failed to import risks (XLSX): ' + (err.message || err));
      }
    };
    input.click();
  };

  // Export current risks as Excel (XLSX) with styled header
  const handleExportXLSX = async () => {
    try {
      if (!riskList || riskList.length === 0) {
        toast.error('No risks to export');
        return;
      }

      // Dynamically import exceljs to avoid increasing initial bundle
      let ExcelJS: any;
      try {
        ExcelJS = (await import('exceljs')).default || (await import('exceljs'));
      } catch (e) {
        toast.error('Excel export requires exceljs. Please install it with "npm i exceljs".');
        return;
      }

      // Collect keys
      const keySet = new Set<string>();
      for (const r of riskList) Object.keys(r || {}).forEach(k => keySet.add(k));

      // Build headers to mirror CSV export behavior (keep in sync)
      const preferredOrder = [
        'componentId', 'riskID','risk', 'lastReviewDate', 'nextReviewDate','riskRating','dateAdded','category','owner','criticality','response','treatmentRequired',
        'nextReviewDate','description','mitigation','type','dataClassification','subprocessor','publishInPrivacyPolicy','annualSecurityReviewCompleted','vendorManagementNeeded',
        'dataTypesShared','dataStorageLocations','portable','serialNumber','physicalLabel','socCriticality','menus'
      ];
      const remaining = Array.from(keySet)
        .filter(k => !preferredOrder.includes(k) && k !== 'history' && k !== 'riskHistory' && k !== 'menus' && k !== 'riskIDStatus' && k !== 'status' && k !== 'riskIDstatus' && k !== 'nextReview' && k !== 'lastReview')
        .sort();
      const headers = [...preferredOrder.filter(k => keySet.has(k)), ...remaining];

      // Normalization (reuse logic similar to CSV)
      const normalizeValue = (key: string, value: any) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'string') {
          const lower = value.trim().toLowerCase();
          if (lower === 'true') return 'Yes';
          if (lower === 'false') return 'No';
        }
        if (key.toLowerCase().includes('date')) {
          try {
            const d = new Date(value);
            if (!isNaN(d.getTime())) return d.toISOString();
          } catch {}
        }
        return value;
      };

      // Create workbook/worksheet
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Risk Register');

      // Header row with style
      ws.addRow(headers);
      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true, size: 12 } as any;
      headerRow.alignment = { vertical: 'middle' } as any;

      // Data rows
      for (const risk of riskList) {
        const row = headers.map((key) => normalizeValue(key, (risk as any)[key]));
        ws.addRow(row);
      }

      // Auto width based on header length and sample data
      const colCount = headers.length;
      for (let c = 1; c <= colCount; c++) {
        const header = headers[c - 1] || '';
        let maxLen = Math.max(10, String(header).length + 2);
        for (let r = 2; r <= Math.min(ws.rowCount, 50); r++) { // sample first 50 rows for perf
          const v = ws.getRow(r).getCell(c).value;
          const s = (typeof v === 'object' && v && 'text' in (v as any)) ? (v as any).text : (v ?? '').toString();
          maxLen = Math.max(maxLen, s.length);
        }
        ws.getColumn(c).width = Math.min(60, maxLen);
      }

      // Write and trigger download
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `risk-register-export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Risks exported as Excel');
    } catch (err) {
      console.error('Failed to export risks as Excel:', err);
      toast.error('Failed to export risks as Excel');
    }
  };

  // Export current risks as CSV file (handles commas, quotes, newlines, UTF-8 BOM for Excel)
  const handleExportCSV = () => {
    try {
      if (!riskList || riskList.length === 0) {
        toast.error('No risks to export');
        return;
      }

      // Collect all keys across all risks to ensure full coverage
      const keySet = new Set<string>();
      for (const r of riskList) Object.keys(r || {}).forEach(k => keySet.add(k));

      // Provide a preferred order for common fields, then append the rest alphabetically
      const preferredOrder = [
        'componentId', 'riskID','risk', 'lastReviewDate', 'nextReviewDate','riskRating','dateAdded','category','owner','criticality','response','treatmentRequired',
        'nextReviewDate','description','mitigation','type','dataClassification','subprocessor','publishInPrivacyPolicy','annualSecurityReviewCompleted','vendorManagementNeeded',
        'dataTypesShared','dataStorageLocations','portable','serialNumber','physicalLabel','socCriticality','menus'
      ];
      // Exclude internal-only fields like 'history' from CSV headers
      const remaining = Array.from(keySet)
        .filter(k => !preferredOrder.includes(k) && k !== 'history' && k !== 'riskHistory' && k !== 'menus' && k !== 'riskIDStatus' && k !== 'status' && k !== 'riskIDstatus' && k !== 'nextReview' && k !== 'lastReview' )
        .sort();
      const headers = [...preferredOrder.filter(k => keySet.has(k)), ...remaining];

      // Escapes a single field for CSV (RFC 4180 style)
      const esc = (val: any) => {
        if (val === null || val === undefined) return '';
        // Preserve human-friendly boolean rendering
        if (typeof val === 'boolean') return val ? 'Yes' : 'No';
        // Numeric boolean-like values
        if (typeof val === 'number') {
          if (val === 1) return 'Yes';
          if (val === 0) return 'No';
        }
        // If provided as a string "true"/"false" (any case), convert to Yes/No
        if (typeof val === 'string') {
          const lower = val.trim().toLowerCase();
          if (lower === 'true') return 'Yes';
          if (lower === 'false') return 'No';
          if (lower === '1') return 'Yes';
          if (lower === '0') return 'No';
        }
        if (val instanceof Date) return val.toISOString();
        if (Array.isArray(val))
          return esc(
            val
              .map(v =>
                v === true ? 'Yes'
                : v === false ? 'No'
                : (typeof v === 'string' && v.trim().toLowerCase() === 'true') ? 'Yes'
                : (typeof v === 'string' && v.trim().toLowerCase() === 'false') ? 'No'
                : (typeof v === 'string' && v.trim().toLowerCase() === '1') ? 'Yes'
                : (typeof v === 'string' && v.trim().toLowerCase() === '0') ? 'No'
                : (typeof v === 'number' && v === 1) ? 'Yes'
                : (typeof v === 'number' && v === 0) ? 'No'
                : v == null ? ''
                : String(v)
              )
              .join('; ')
          );
        if (typeof val === 'object') {
          try { return esc(JSON.stringify(val)); } catch { return esc(String(val)); }
        }
        const s = String(val);
        if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
        return s;
      };

      // Normalize potential date-like strings
      const normalizeValue = (key: string, value: any) => {
        // Preserve empty string but normalize null/undefined to empty cell
        if (value === null || value === undefined) return '';
        // Convert booleans to human-friendly Yes/No
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        // Handle string boolean-like values
        if (typeof value === 'string') {
          const lower = value.trim().toLowerCase();
          if (lower === 'true') return 'Yes';
          if (lower === 'false') return 'No';
        }
        if (key.toLowerCase().includes('date')) {
          try {
            const d = new Date(value);
            if (!isNaN(d.getTime())) return d.toISOString();
          } catch {}
        }
        return value;
      };

      // Build CSV rows
      const rows: string[] = [];
      rows.push(headers.join(','));
      for (const risk of riskList) {
        const row = headers.map((key) => esc(normalizeValue(key, (risk as any)[key])));
        rows.push(row.join(','));
      }

      const csvString = rows.join('\n');
      const blob = new Blob(["\uFEFF" + csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `risk-register-export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Risks exported as CSV');
    } catch (err) {
      console.error('Failed to export risks as CSV:', err);
      toast.error('Failed to export risks as CSV');
    }
  };

  // --- JSON Import/Export Handlers ---
  // Robust CSV parser (handles quotes, commas, CR/LF)
  const parseCSV = (text: string): string[][] => {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const next = text[i + 1];
      if (inQuotes) {
        if (char === '"') {
          if (next === '"') {
            field += '"';
            i++; // skip escaped quote
          } else {
            inQuotes = false;
          }
        } else {
          field += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          row.push(field);
          field = '';
        } else if (char === '\n') {
          row.push(field);
          rows.push(row);
          row = [];
          field = '';
        } else if (char === '\r') {
          // Ignore CR, handle CRLF by letting the \n branch add the row
        } else {
          field += char;
        }
      }
    }
    // push last field/row
    row.push(field);
    rows.push(row);
    // Trim trailing empty row if file ended with newline
    if (rows.length && rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === '') {
      rows.pop();
    }
    return rows;
  };

  // Import risks from a CSV file
  const handleImportCSV = () => {
    if (!tenancyId) {
      toast.error('No active tenancy selected');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const rows = parseCSV(text);
        if (!rows.length) throw new Error('CSV contains no data');
        const headers = rows[0].map(h => h.trim());
        const dataRows = rows.slice(1).filter(r => r.some(cell => cell && cell.trim() !== ''));
        // Helper to coerce common boolean text values from CSV
        const coerceFromCSV = (val: string) => {
          if (val == null) return '';
          const trimmed = val.trim();
          if (trimmed === '') return '';
          const lower = trimmed.toLowerCase();
          if (lower === 'yes' || lower === 'true') return true;
          if (lower === 'no' || lower === 'false') return false;
          return trimmed;
        };
        const imported = dataRows.map(cols => {
          const obj: Record<string, any> = {};
          headers.forEach((h, idx) => {
            obj[h] = coerceFromCSV(cols[idx] ?? '');
          });
          return obj;
        });

        const normalizedImported: Record<string, any>[] = imported.map((risk: Record<string, any>) => ({
          ...risk,
          selectedControls: normalizeControlList(risk.selectedControls),
          relatedControls: normalizeControlList(risk.relatedControls),
        }));

        // Validate required fields for each risk
        const valid = normalizedImported.every((r: any) => r.risk && r.category);
        if (!valid) throw new Error('Some risks are missing required fields (risk or category)');

        // --- Backend Sync: Persist imported risks ---
        // Get the current risks for ID comparison
        let currentRisks = [...riskList];
        const existingRiskIds = new Set(currentRisks.map(risk => risk.riskID));

        // Track max ID for new risks
        let maxId = 0;
        [...currentRisks, ...normalizedImported].forEach(risk => {
          const idNum = parseInt(risk.riskID || '0', 10);
          if (!isNaN(idNum) && idNum > maxId) maxId = idNum;
        });

        // Process each imported risk
        for (const importedRisk of normalizedImported) {
          const rawRiskId = importedRisk.riskID;
          let riskId = typeof rawRiskId === 'string' ? rawRiskId.replaceAll('RSK-','') : rawRiskId != null ? rawRiskId.toString() : '';
          if (!riskId || !existingRiskIds.has(riskId)) {
            // This is a new risk, create a new ID
            maxId++;
            riskId = maxId.toString();
            importedRisk.riskID = riskId;

            const newRisk = {
              ...importedRisk,
              componentId: importedRisk.componentId || `RSK-${riskId}`,
              riskIDstatus: 'active',
              selectedControls: normalizeControlList(importedRisk.selectedControls),
              relatedControls: normalizeControlList(importedRisk.relatedControls),
            };

            const newRiskPlain = JSON.parse(JSON.stringify(newRisk));

            try {
              await createRisk(tenancyId, riskId, newRiskPlain as any);
            } catch (err) {
              console.error('Failed to create new risk:', err);
            }
          } else {
            // Update existing risk
            try {
              const updatePayload = {
                ...importedRisk,
                selectedControls: normalizeControlList(importedRisk.selectedControls),
                relatedControls: normalizeControlList(importedRisk.relatedControls),
                // companyID: tenancyId
              };
              const updatePlain = JSON.parse(JSON.stringify(updatePayload));
              await putRisk(tenancyId, riskId, updatePlain as any);
            } catch (err) {
              console.error('Failed to update risk:', err);
            }
          }
        }

        // Reload risks from backend
        getAllRisks(tenancyId)
          .then((risks) => {
            const nonDeletedRisks = risks.filter((risk: any) => risk.riskIDstatus !== "deleted");
            setRiskList(nonDeletedRisks);

            // Update filter options
            setRiskRatingOptions(Array.from(new Set(nonDeletedRisks.map(a => a.riskRating).map(a => a.toLowerCase()).map(a => a.charAt(0).toUpperCase() + a.slice(1)))));
            setCategoryOptions(Array.from(new Set(nonDeletedRisks.map(a => a.category).map(a => a.toLowerCase()).map(a => a.charAt(0).toUpperCase() + a.slice(1)))));
            setResponseOptions(Array.from(new Set(nonDeletedRisks.map(a => a.response).map(a => a.toLowerCase()).map(a => a.charAt(0).toUpperCase() + a.slice(1)))));

            toast.success('Risks imported and synced successfully');
          })
          .catch(err => {
            console.error('Failed to refresh risks after import:', err);
            toast.error('Imported risks but failed to refresh the display');
          });
      } catch (err: any) {
        toast.error('Failed to import risks (CSV): ' + (err.message || err));
      }
    };
    input.click();
  };
  // Export current risks as JSON file
  const handleExportJSON = () => {
    try {
      // Remove any undefined fields for cleanliness
      const cleanedRisks = riskList.map(risk => JSON.parse(JSON.stringify(risk)));
      const jsonString = JSON.stringify(cleanedRisks, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `risk-register-export_${new Date().toISOString().split('T')[0]}.jsonc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Risks exported as JSON');
    } catch (err) {
      toast.error('Failed to export risks as JSON');
    }
  };

  // Import risks from a JSON file
  const handleImportJSON = () => {
    if (!tenancyId) {
      toast.error('No active tenancy selected');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json,.jsonc,application/jsonc';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const imported = JSON.parse(text);
        if (!Array.isArray(imported)) throw new Error('JSON must be an array of risk objects');

        const normalizedImported: Record<string, any>[] = imported.map((risk: Record<string, any>) => ({
          ...risk,
          selectedControls: normalizeControlList(risk.selectedControls),
          relatedControls: normalizeControlList(risk.relatedControls),
        }));

        // Validate required fields for each risk
        const valid = normalizedImported.every((r: any) => r.risk && r.category);
        if (!valid) throw new Error('Some risks are missing required fields (risk or category)');

        // --- Backend Sync: Persist imported risks ---
        // Get the current risks for ID comparison
        let currentRisks = [...riskList];

        // Create a map of existing risk IDs
        const existingRiskIds = new Set(currentRisks.map(risk => risk.riskID));

        // Track max ID for new risks
        let maxId = 0;
        [...currentRisks, ...normalizedImported].forEach(risk => {
          const idNum = parseInt(risk.riskID || '0', 10);
          if (!isNaN(idNum) && idNum > maxId) maxId = idNum;
        });

        // Process each imported risk
        for (const importedRisk of normalizedImported) {
          let riskId = importedRisk.riskID;
          if (!riskId || !existingRiskIds.has(riskId)) {
            // This is a new risk, create a new ID
            maxId++;
            riskId = maxId.toString();
            importedRisk.riskID = riskId;

            // For new risks, ensure we have all required fields
            const newRisk = {
              ...importedRisk,
              companyID: tenancyId,
              selectedControls: normalizeControlList(importedRisk.selectedControls),
              relatedControls: normalizeControlList(importedRisk.relatedControls),
            };

            const newRiskPlain = JSON.parse(JSON.stringify(newRisk));

            try {
              // Create new risk using putRisk with a new ID
              await createRisk(tenancyId, riskId, newRiskPlain as any);
            } catch (err) {
              console.error('Failed to create new risk:', err);
            }
          } else {
            // Update existing risk
            try {
              const updatePayload = {
                ...importedRisk,
                companyID: tenancyId,
                selectedControls: normalizeControlList(importedRisk.selectedControls),
                relatedControls: normalizeControlList(importedRisk.relatedControls),
              };
              const updatePlain = JSON.parse(JSON.stringify(updatePayload));
              await putRisk(tenancyId, riskId, updatePlain as any);
            } catch (err) {
              console.error('Failed to update risk:', err);
            }
          }
        }

        // Reload risks from backend
        getAllRisks(tenancyId)
          .then((risks) => {
            const nonDeletedRisks = risks.filter((risk: any) => risk.riskIDstatus !== "deleted");
            setRiskList(nonDeletedRisks);

            // Update filter options
            setRiskRatingOptions(Array.from(new Set(nonDeletedRisks.map(a => a.riskRating).map(a => a.toLowerCase()).map(a => a.charAt(0).toUpperCase() + a.slice(1)))));
            setCategoryOptions(Array.from(new Set(nonDeletedRisks.map(a => a.category).map(a => a.toLowerCase()).map(a => a.charAt(0).toUpperCase() + a.slice(1)))));
            setResponseOptions(Array.from(new Set(nonDeletedRisks.map(a => a.response).map(a => a.toLowerCase()).map(a => a.charAt(0).toUpperCase() + a.slice(1)))));

            toast.success('Risks imported and synced successfully');
          })
          .catch(err => {
            console.error('Failed to refresh risks after import:', err);
            toast.error('Imported risks but failed to refresh the display');
          });
      } catch (err: any) {
        toast.error('Failed to import risks: ' + (err.message || err));
      }
    };
    input.click();
  };

  /**
   * JSON Structure Example (for documentation):
   * [
   *   {
   *     "riskID": "101",
   *     "risk": "Data breach",
   *     "description": "Unauthorized access to customer data",
   *     "riskRating": "High",
   *     "category": "IT",
   *     "response": "Mitigate",
   *     "status": "Active",
   *     "nextReviewDate": "2023-12-31T00:00:00.000Z",
   *     "mitigation": "Implement encryption and access controls"
   *   },
   *   ...
   * ]
   */

  // Button style for active filter
  const getFilterButtonClass = (active: boolean) =>
    active
      ? 'filter-btn-active border font-semibold bg-primary-fade text-[color:var(--primary)] border-primary-fade-border'
      : 'border border-border bg-transparent text-foreground';

  const onActions = {
    onEdit: () => { },
    onView: () => { },
  }

  useEffect(() => {
    if (!isAuthLoaded()) {
      const unsub = onAuthReady(() => {
        setUid(getAuthUID());
      });

      return unsub;
    }
  }, []);

  useEffect(() => {
    if (!tenancyId) return;

    setIsLoading(true);
    getAllRisks(tenancyId)
      .then((risks) => {
        console.log('[BBB]', risks);
        const nonDeletedRisks = risks.filter((risk: any) => risk.riskIDstatus !== "deleted");
        setRiskList(nonDeletedRisks);

        // Helper function to process options
        const processOptions = (items: any[]) => {
          return Array.from(new Set(items
            .filter(item => item != null && item.toString().trim() !== '') // Filter out null, undefined, and empty strings
            .map(item => item.toString().toLowerCase())
            .map(item => item.charAt(0).toUpperCase() + item.slice(1))
          ));
        };

        // Process and set options
        setRiskRatingOptions(processOptions(nonDeletedRisks.map(a => a.riskRating)));
        setCategoryOptions(processOptions(nonDeletedRisks.map(a => a.category)));
        setResponseOptions(processOptions(nonDeletedRisks.map(a => a.response)));
      })
      .catch(error => {
        console.error('Error fetching risks:', error);
        toast.error('Failed to load risk data');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [tenancyId]);

  useEffect(() => {
    getHelpContentByPage('policy.admin').then((content) => {
      console.log('getHelpContentByPage', content['page.title']);
      if (content) {
        setHelpContent(content);
      }
    });
  }, []);

  return (
    <div className="page-container">
      <div className="glass-card">
        <div className="header-row flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="title flex flex-row">
              Risk Register
              <span className="flex items-center bg-transparent pl-2 rounded font-semibold text-center">
                <HelpPopoverHover config={helpContent?.['page.title']} />
              </span>
            </h2>
            <p className="subtitle">
              This is your central command centre for managing
              risk, allowing you to log, assign, link, monitor and
              maintain risk to your organisation.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 md:flex-nowrap">
            {!isReadOnly && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="import-btn" variant="outline" size="sm" onClick={handleImportCSV}>
                      <Upload size={16} style={{ marginRight: 4 }} /> Import
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-card">
                    <DropdownMenuItem onClick={handleImportCSV}>Import CSV</DropdownMenuItem>
                    <DropdownMenuItem onClick={handleImportXLSX}>Import Excel (XLSX)</DropdownMenuItem>
                    <DropdownMenuItem onClick={handleImportJSON}>Import JSON</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button className="primary-btn" size="sm" onClick={() => navigate('/riskdetail/new')} style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}><Plus className="mr-2 h-4 w-4" /> Add Risk</Button>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="export-btn" variant="outline" size="sm" onClick={handleExportCSV}>
                  <Download size={16} style={{ marginRight: 4 }} /> Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card">
                <DropdownMenuItem onClick={handleExportCSV}>Export as CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportXLSX}>Export as Excel (XLSX)</DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportJSON}>Export as JSON</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="filters-row flex flex-col md:flex-row md:items-center md:gap-4 md:justify-between my-5">
          {/* Search Bar */}
          <div className={`w-full md:w-auto md:max-w-xs ${isMobile ? 'mb-3' : ''}`}>
            <Input
              className="search-bar w-full"
              placeholder="Search Risks"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              // Icon removed due to component compatibility
            />
          </div>

          {/* Filter Controls */}
          <div className={`filter-controls ${isMobile ? 'mt-3' : 'mt-0'} flex flex-wrap gap-2 justify-center md:justify-end md:w-auto`}>
            {/* All Risks Button */}
            <Button
              variant="outline"
              size="sm"
              className={getFilterButtonClass(riskRatingFilter === 'all' && categoryFilter === 'all' && responseFilter === 'all') + ' h-9 px-4 rounded-[var(--radius)] flex items-center gap-2'}
              onClick={() => {
                setRiskRatingFilter('all');
                setCategoryFilter('all');
                setResponseFilter('all');
              }}
            >
              All Risks
            </Button>

            {/* Risk Rating Dropdown */}
            <Select value={riskRatingFilter} onValueChange={v => setRiskRatingFilter(v as typeof riskRatingFilter)}>
              <SelectTrigger className={getFilterButtonClass(riskRatingFilter !== 'all') + ' min-w-[120px] rounded-[var(--radius)] flex items-center gap-2'}>
                <span>
                  {riskRatingFilter !== 'all' ? `Risk Rating: ${riskRatingFilter}` : 'Risk Rating'}
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {riskRatingOptions.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Business Unit Dropdown */}
            <Select value={categoryFilter} onValueChange={v => setCategoryFilter(v as typeof categoryFilter)}>
              <SelectTrigger className={getFilterButtonClass(categoryFilter !== 'all') + ' min-w-[120px] rounded-[var(--radius)] flex items-center gap-2'}>
                <span>
                  {categoryFilter !== 'all' ? `Business Unit: ${categoryFilter}` : 'Business Unit'}
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {categoryOptions.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Response Dropdown */}
            <Select value={responseFilter} onValueChange={v => setResponseFilter(v as typeof responseFilter)}>
              <SelectTrigger className={getFilterButtonClass(responseFilter !== 'all') + ' min-w-[120px] rounded-[var(--radius)] flex items-center gap-2'}>
                <span>
                  {responseFilter !== 'all' ? `Response: ${responseFilter}` : 'Response'}
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {responseOptions.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        </div>

        {/* Responsive Risk List: DataTable for desktop, Cards for mobile */}
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Loading risks...</span>
              </div>
            </div>
          )}
          <div className="risk-list">
            <div className="hidden md:block">
              <DataTable
                columns={getRiskColumns(navigate, {
                  onEdit: isReadOnly ? undefined : onActions.onEdit,
                  onView: onActions.onView,
                }, helpContent, basePath, userIdToName)}
                data={sortedRisks}
                pageSize={10}
                columnDisplayConfig={riskRegisterColumnConfig}
                onRowClick={(risk) => {
                  // If using a custom path, navigate directly, otherwise use route by name
                  if (basePath !== '/risk-detail') {
                    navigate(`${basePath}/${risk.riskID}`);
                  } else {
                    navigate(routePathByName('risk.detail', { id: risk.riskID }));
                  }
                }}
              />
            </div>
          <div className="block md:hidden">
            { /* /!* Mobile Cards *!/ */}
            {sortedRisks.map(risk => {
              try {
                return (
                  <div className="mobile-card" key={risk.id ?? 'unknown'} style={{
                    backgroundColor: theme.isDark ? '#18181b' : 'white',
                    color: theme.isDark ? '#f3f4f6' : '#18181b',
                    border: `1px solid ${theme.isDark ? '#27272a' : '#e5e7eb'}`,
                    boxShadow: theme.isDark ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <div className="mobile-card-header">
                      <span className="mobile-card-id">ID: {risk.id ?? 'N/A'}</span>
                    </div>
                    <div className="mobile-card-field"><strong>Business Unit:</strong> {risk.category ?? 'N/A'}</div>
                    <div className="mobile-card-field"><strong>Risk:</strong> {risk.risk ?? 'N/A'}</div>
                    <div className="mobile-card-field"><strong>Risk Rating:</strong> {risk.riskRating ? <Badge variant={getBadgeVariant(risk.riskRating)}>{risk.riskRating}</Badge> : 'N/A'}</div>
                    <div className="mobile-card-field"><strong>Response:</strong> {risk.response ?? 'N/A'}</div>
                    <div className="mobile-card-field"><strong>Next Review Date:</strong> {renderDate(risk.nextReviewDate) ?? 'N/A'}</div>
                    <Button variant="ghost" size="sm" onClick={() => {
                      // If using a custom path, navigate directly, otherwise use route by name
                      if (basePath !== '/risk-detail') {
                        navigate(`${basePath}/${risk.riskID}`);
                      } else {
                        navigate(routePathByName('risk.detail', { id: risk.riskID }));
                      }
                    }}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                );
              } catch (err) {
                return (
                  <div className="mobile-card" key={risk.id ?? Math.random()} style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}>
                    <div>Error rendering card</div>
                  </div>
                );
              }
            })}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default RiskRegister;
